import React from 'react';

import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { ClaimDocumentMetadata } from '~/components/Chat/ChatContextDialog';
import ChatContextDialog from '~/components/Chat/ChatContextDialog/ChatContextDialog';
import ChatMessage from '~/components/Chat/ChatMessage';
import { ChatSkeleton } from '~/components/Chat/ChatSkeleton';
import CliveChatMessage from '~/components/Chat/CliveChatMessage';
import { NEW_CHAT_TOKEN } from '~/components/Chat/constants';
import { useClaimChatsContext } from '~/components/Chat/context/ChatContext';
import { UserAvatar } from '~/components/Chat/UserAvatar';
import ChatInput, { ChatInputAction } from '~/components/ChatInput/ChatInput';
import { useClaimDocuments } from '~/components/Documents';
import { useAuth } from '~/context/AuthContext';
import AtIcon from '~/core/iconComponents/icons/AtIcon';
import DocumentIcon from '~/core/iconComponents/icons/DocumentIcon';
import cn from '~/utils/cn';
import { parseDateWithOptionalTimezone } from '~/utils/Dates/DateTimeUtils';

const Chat: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { claimNumber = '', chatId: chatIdFromRoute } = useParams();
  const [value, setValue] = React.useState('');
  const [initialized, setInitialized] = React.useState(false);
  const [isChatPageLoading, setIsChatPageLoading] = React.useState(true);
  const [withTypingEffect, setWithTypingEffect] = React.useState(false);
  const endOfChatRef = React.useRef<HTMLDivElement>(null);
  const [isChatContextDialogOpen, setIsChatContextDialogOpen] = React.useState(false);
  const [selectedContextDocuments, setSelectedContextDocuments] = React.useState<ClaimDocumentMetadata[]>([]);

  const { isChatLoading, getChatExchanges, createChatExchange, exchanges, resetExchanges } = useClaimChatsContext();
  const { user } = useAuth();
  const { documents: claimDocuments, isLoading: isDocumentsLoading } = useClaimDocuments({ claimNumber });

  const isNewChat = React.useMemo(() => chatIdFromRoute === NEW_CHAT_TOKEN, [chatIdFromRoute]);

  const getChatId = (isNewChat: boolean, chatIdFromRoute: string | undefined): string | null => {
    return isNewChat ? null : chatIdFromRoute || null;
  };

  const scrollDown = React.useCallback(() => {
    setTimeout(() => {
      if (endOfChatRef.current) {
        endOfChatRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 500);
  }, [endOfChatRef]);

  const updateUrl = React.useCallback(
    async (newChatId?: string) => {
      if (!claimNumber || !newChatId) return;
      if (chatIdFromRoute !== newChatId) {
        await navigate(`/claims/${claimNumber}/chat/${newChatId}`);
      }
    },
    [chatIdFromRoute, claimNumber, navigate]
  );

  const chatInputActions: ChatInputAction[] = React.useMemo(
    () => [
      {
        key: 'choose-claim-documents',
        icon: <AtIcon className="h-20 w-20 text-blue-900" />,
        onClick: () => setIsChatContextDialogOpen(true),
        ariaLabel: 'Choose claim documents',
        disabled: !claimNumber || isChatLoading || isDocumentsLoading,
      },
    ],
    [claimNumber, isChatLoading, isDocumentsLoading]
  );

  const sendChatMessage = React.useCallback(
    async (message?: string) => {
      if (!claimNumber || !message?.trim() || isChatLoading) return;
      setWithTypingEffect(true);
      setValue('');
      scrollDown();
      const chatId = await createChatExchange({
        claimNumber,
        userInput: message,
        chatId: getChatId(isNewChat, chatIdFromRoute),
        usedDocuments: selectedContextDocuments.map((doc) => doc.id),
      });
      scrollDown();
      await updateUrl(chatId);
      return chatId;
    },
    [
      claimNumber,
      createChatExchange,
      isNewChat,
      chatIdFromRoute,
      updateUrl,
      scrollDown,
      isChatLoading,
      selectedContextDocuments,
    ]
  );

  const loadChat = React.useCallback(
    async (chatId?: string) => {
      if (!claimNumber) return;

      setWithTypingEffect(false);
      await getChatExchanges(claimNumber, chatId || chatIdFromRoute);
    },
    [claimNumber, getChatExchanges, chatIdFromRoute]
  );

  const initializeChat = React.useCallback(async () => {
    if (!claimNumber || initialized) {
      return;
    }

    setInitialized(true);
    resetExchanges();

    let newChatId;
    if (isNewChat) {
      setIsChatPageLoading(false);
      newChatId = await sendChatMessage(location?.state?.userMessage);
    }

    await loadChat(newChatId);
    setIsChatPageLoading(false);
    scrollDown();
  }, [
    claimNumber,
    initialized,
    isNewChat,
    loadChat,
    resetExchanges,
    sendChatMessage,
    scrollDown,
    location?.state?.userMessage,
  ]);

  React.useEffect(() => {
    if (!initialized) {
      initializeChat();
    }
  }, [initialized, initializeChat]);

  const isLastExchange = (index: number) => {
    return exchanges.length - 1 === index;
  };

  return (
    <div className="__CHAT_PAGE__ bg-gradient-3 flex-1 overflow-auto bg-cover px-20">
      <div className="__CHAT_CONTAINER__ flex h-full w-full justify-center overflow-auto">
        <div className="__CHAT_SCROLABLE__ flex w-full max-w-3xl flex-col gap-20 py-20">
          {isChatPageLoading ? (
            <ChatSkeleton />
          ) : (
            <>
              {exchanges.map((exchange, index) => (
                <div key={exchange.id} className="mb-20 flex flex-col items-end gap-12">
                  <ChatMessage
                    message={exchange.user_input}
                    sender="You"
                    className="bg-bluesky-700/5 rounded-lg"
                    createdAt={parseDateWithOptionalTimezone(exchange.created_at)}
                    avatar={<UserAvatar user={user} />}
                    direction="rtl"
                    claimNumber={claimNumber}
                    documentsUsed={exchange.context_document_source_ids}
                  />
                  <CliveChatMessage
                    message={exchange.response}
                    isLoading={isChatLoading && isLastExchange(index)}
                    withTypingEffect={isLastExchange(index) && withTypingEffect}
                    onEndTypingEffect={scrollDown}
                    claimNumber={claimNumber}
                    documentsUsed={exchange.documents_used}
                  />
                </div>
              ))}
            </>
          )}

          <div className="__CHAT_SPACER__ min-h-[200px]" ref={endOfChatRef} />

          <div className="__CHAT_INPUT_CONTAINER__ fixed bottom-20 w-full max-w-3xl">
            <ChatInput
              value={value}
              rows={1}
              onTextChange={(e) => setValue(e.target.value)}
              onSubmit={sendChatMessage}
              className={cn('__CHAT_INPUT__ w-full')}
              placeholder="Ask me anything..."
              disableSubmit={isChatLoading}
              actions={chatInputActions}
              tags={selectedContextDocuments.map((doc) => ({
                id: doc.id,
                text: doc.document_name || '',
                icon: <DocumentIcon className="h-16 w-16 text-slate-800" />,
              }))}
              onTagClose={(tagId) =>
                setSelectedContextDocuments(selectedContextDocuments.filter((doc) => doc.id !== tagId))
              }
            />
          </div>
        </div>
      </div>
      {isChatContextDialogOpen ? (
        <ChatContextDialog
          isOpen
          onClose={() => setIsChatContextDialogOpen(false)}
          documents={claimDocuments}
          selected={selectedContextDocuments}
          onSubmit={(selectedDocuments) => {
            setSelectedContextDocuments(selectedDocuments);
            setIsChatContextDialogOpen(false);
          }}
        />
      ) : null}
    </div>
  );
};

export default Chat;
